import React, { useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Select, 
  DatePicker, 
  InputNumber, 
  Switch, 
  Radio, 
  Checkbox,
  Card,
  Row,
  Col,
  Divider,
  Typography
} from 'antd';
import { DynamicFormProps, FormFieldConfig } from './types';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Group: RadioGroup } = Radio;
const { Group: CheckboxGroup } = Checkbox;

/**
 * 动态表单组件
 * 根据配置生成表单
 */
const DynamicForm: React.FC<DynamicFormProps> = ({
  config,
  initialValues = {},
  onFinish,
  onCancel,
  loading = false,
  mode = 'create',
  form: externalForm
}) => {
  // Only create an internal form if no external form is provided
  const [form] = Form.useForm();
  const formInstance = externalForm || form;
  const isViewMode = mode === 'view';

  // 设置初始值
  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [formInstance, initialValues]);

  // 根据字段类型渲染对应的表单控件
  const renderFormItem = (field: FormFieldConfig) => {
    const { key, type, placeholder, options, props = {} } = field;
    
    // 如果是只读模式，所有字段都禁用
    const disabled = isViewMode || props.disabled;

    switch (type) {
      case 'input':
        return <Input placeholder={placeholder} disabled={disabled} {...props} />;
      
      case 'textarea':
        return <TextArea placeholder={placeholder} disabled={disabled} rows={4} {...props} />;
      
      case 'number':
        return <InputNumber placeholder={placeholder} disabled={disabled} style={{ width: '100%' }} {...props} />;
      
      case 'select':
        return (
          <Select placeholder={placeholder} disabled={disabled} {...props}>
            {options?.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );
      
      case 'date':
        return <DatePicker disabled={disabled} style={{ width: '100%' }} {...props} />;
      
      case 'dateRange':
        return <DatePicker.RangePicker disabled={disabled} style={{ width: '100%' }} {...props} />;
      
      case 'switch':
        return <Switch disabled={disabled} {...props} />;
      
      case 'radio':
        return (
          <RadioGroup disabled={disabled} {...props}>
            {options?.map(option => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </RadioGroup>
        );
      
      case 'checkbox':
        return (
          <CheckboxGroup disabled={disabled} {...props}>
            {options?.map(option => (
              <Checkbox key={option.value} value={option.value}>
                {option.label}
              </Checkbox>
            ))}
          </CheckboxGroup>
        );
      
      default:
        return <Input placeholder={placeholder} disabled={disabled} {...props} />;
    }
  };

  // 处理表单提交
  const handleFinish = (values: Record<string, any>) => {
    if (onFinish) {
      onFinish(values);
    }
  };

  return (
    <Card>
      <Title level={4}>{config.name}</Title>
      {config.description && <Text type="secondary">{config.description}</Text>}
      
      <Divider />
      
      <Form
        form={formInstance}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={initialValues}
        disabled={isViewMode}
      >
        <Row gutter={[16, 0]}>
          {config.fields.map(field => (
            <Col key={field.key} xs={24} sm={24} md={12} lg={8}>
              <Form.Item
                name={field.key}
                label={field.label}
                rules={field.rules}
                required={field.required}
                tooltip={field.props?.tooltip}
              >
                {renderFormItem(field)}
              </Form.Item>
            </Col>
          ))}
        </Row>
        
        {!isViewMode && (
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === 'create' ? '提交' : '更新'}
            </Button>
            {onCancel && (
              <Button style={{ marginLeft: 8 }} onClick={onCancel}>
                取消
              </Button>
            )}
          </Form.Item>
        )}
        
        {isViewMode && onCancel && (
          <Button onClick={onCancel}>返回</Button>
        )}
      </Form>
    </Card>
  );
};

export default DynamicForm; 